/**
 * Carga y autoguardado del borrador de "Mis datos".
 * Al montar: borrador remoto (RLS) vs local → gana el más reciente; si no hay ninguno, se siembra desde la foto vigente o la plantilla.
 * Cada cambio: respaldo local inmediato (300 ms) y PUT /api/drafts con debounce (1.5 s). Sin red: queda en el dispositivo.
 */
import { useCallback, useEffect, useRef } from "react";
import { migrarInputs } from "@/engine/migrate";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { api, ApiError } from "@/lib/api";
import { emptyInputs, VERSIONS } from "@/engine/versions";
import type { ModelInputs } from "@/engine/types";
import type { MemberRow, SnapshotDraftRow, SnapshotRow } from "@/types/db";
import { hayPendientes, useDatos, type SeedUpload } from "../store/store";
import { normalizarPrioridades, prioridadesLimpias } from "../store/acciones";
import { borrarLocal, escribirLocal, leerLocal } from "./local";

export function useBorradorRemoto(enabled = true) {
  return useQuery({
    queryKey: ["draft"],
    enabled,
    queryFn: async (): Promise<SnapshotDraftRow | null> => {
      const { data, error } = await supabase.from("snapshot_drafts").select("*").maybeSingle();
      if (error) throw error;
      return (data as SnapshotDraftRow | null) ?? null;
    },
  });
}

/** Prepara inputs sembrados: prioridades limpias (avisando). El nombre ya no se prellena: la app no guarda nombres (2-nov-2026). */
export function prepararSemilla(inputs: ModelInputs): { inputs: ModelInputs; avisos: string[] } {
  const avisos: string[] = [];
  let i = inputs;
  if (!prioridadesLimpias(i)) { i = normalizarPrioridades(i); avisos.push("Ordené tus objetivos por prioridad (había prioridades repetidas o vacías). Revísalos en Tus objetivos."); }
  return { inputs: i, avisos };
}

export function useBorrador(member: MemberRow, snapshotActual: { row: SnapshotRow } | null | undefined, snapshotCargando: boolean) {
  const remoto = useBorradorRemoto();
  const qc = useQueryClient();
  const meta = useDatos((s) => s.meta);
  const seedCrudo = useDatos((s) => s.seed);
  const setMeta = useDatos((s) => s.setMeta);
  // Todo sembrado queda marcado con el miembro: si entra otro en la misma pestaña, el estado del primero no se reutiliza (#7)
  const seed = useCallback<typeof seedCrudo>((inputs, m) => seedCrudo(inputs, { ...m, cargadoPara: member.id }), [seedCrudo, member.id]);

  // Carga inicial
  useEffect(() => {
    if (meta.cargado && meta.modo === "real" && meta.cargadoPara === member.id) return;
    if (remoto.isLoading || snapshotCargando) return;
    const local = leerLocal(member.id);
    const r = remoto.data;
    const rOk = r && VERSIONS[r.model_version as keyof typeof VERSIONS] ? r : null;
    const localMasNuevo = local && (!rOk || new Date(local.updatedAt) > new Date(rOk.updated_at));
    const tSnap = snapshotActual ? new Date(snapshotActual.row.created_at).getTime() : 0;
    const tBorrador = Math.max(local ? new Date(local.updatedAt).getTime() : 0, rOk ? new Date(rOk.updated_at).getTime() : 0);
    if (snapshotActual && tSnap > tBorrador + 5000) {
      // La foto vigente es más nueva que cualquier borrador (p. ej. se subió un Excel después): se edita desde la foto.
      const { inputs, avisos } = prepararSemilla(migrarInputs(snapshotActual.row.inputs as ModelInputs));
      seed(inputs, { modo: "real", source: "snapshot", seedSnapshotId: snapshotActual.row.id, revisionLocal: 0, revisionGuardada: 0, guardado: "idle", avisos });
    } else if (localMasNuevo && local) {
      const { inputs, avisos } = prepararSemilla(migrarInputs(local.inputs));
      seed(inputs, { modo: "real", source: (local.source as "form") ?? "form", seedSnapshotId: local.seedSnapshotId, revisionLocal: 1, revisionGuardada: 0, guardado: "pendiente", updatedAt: local.updatedAt, avisos });
    } else if (rOk) {
      const { inputs, avisos } = prepararSemilla(migrarInputs(rOk.inputs as ModelInputs));
      seed(inputs, { modo: "real", source: rOk.source, seedSnapshotId: rOk.seed_snapshot_id, seedUpload: (rOk.seed_upload as SeedUpload | null) ?? null, revisionLocal: 0, revisionGuardada: 0, guardado: "guardado", lastSavedAt: rOk.updated_at, updatedAt: rOk.updated_at, avisos });
    } else if (snapshotActual) {
      const { inputs, avisos } = prepararSemilla(migrarInputs(snapshotActual.row.inputs as ModelInputs));
      seed(inputs, { modo: "real", source: "snapshot", seedSnapshotId: snapshotActual.row.id, revisionLocal: 0, revisionGuardada: 0, guardado: "idle", avisos });
    } else {
      const { inputs } = prepararSemilla(emptyInputs());
      seed(inputs, { modo: "real", source: "form", revisionLocal: 0, revisionGuardada: 0, guardado: "idle" });
    }
  }, [meta.cargado, meta.modo, meta.cargadoPara, remoto.isLoading, remoto.data, snapshotCargando, snapshotActual, member, seed]);

  // Autoguardado
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return useDatos.subscribe((s, prev) => {
      if (s.meta.modo !== "real" || !s.meta.cargado) return;
      if (s.meta.revisionLocal === prev.meta.revisionLocal && s.inputs === prev.inputs) return;
      if (!hayPendientes(s.meta)) return;
      escribirLocal(member.id, { inputs: s.inputs, updatedAt: s.meta.updatedAt ?? new Date().toISOString(), source: s.meta.source, seedSnapshotId: s.meta.seedSnapshotId });
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void guardarBorrador(), 1500);
    });
  }, [member.id]);

  // Reintento al volver la red
  useEffect(() => {
    const on = () => { if (hayPendientes(useDatos.getState().meta)) void guardarBorrador(); };
    window.addEventListener("online", on);
    const antes = (e: BeforeUnloadEvent) => { if (hayPendientes(useDatos.getState().meta) && useDatos.getState().meta.guardado !== "local") { e.preventDefault(); } };
    window.addEventListener("beforeunload", antes);
    return () => { window.removeEventListener("online", on); window.removeEventListener("beforeunload", antes); };
  }, []);

  async function guardarBorrador(): Promise<boolean> {
    const st = useDatos.getState();
    if (st.meta.modo !== "real") return false;
    const rev = st.meta.revisionLocal;
    setMeta({ guardado: "guardando", error: null });
    try {
      const u = st.meta.seedUpload;
      await api("/api/drafts", { method: "PUT", json: { modelVersion: st.inputs.version, source: st.meta.source, seedSnapshotId: st.meta.seedSnapshotId, seedUpload: u, inputs: st.inputs } });
      setMeta({ guardado: useDatos.getState().meta.revisionLocal > rev ? "pendiente" : "guardado", revisionGuardada: rev, lastSavedAt: new Date().toISOString() });
      void qc.invalidateQueries({ queryKey: ["draft"] });
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) setMeta({ guardado: "local", error: e.message });
      else if (!navigator.onLine || !(e instanceof ApiError)) setMeta({ guardado: "offline", error: null });
      else setMeta({ guardado: "error", error: e.message });
      return false;
    }
  }

  async function empezarDeCero() {
    try { await api("/api/drafts", { method: "DELETE" }); } catch { /* si falla, igual se limpia local */ }
    borrarLocal(member.id);
    const { inputs } = prepararSemilla(emptyInputs());
    seed(inputs, { modo: "real", source: "form", seedSnapshotId: null, seedUpload: null, revisionLocal: 0, revisionGuardada: 0, guardado: "idle", avisos: [] });
    void qc.invalidateQueries({ queryKey: ["draft"] });
  }

  return { guardarBorrador, empezarDeCero, cargando: !meta.cargado || remoto.isLoading };
}
